import { useEffect, useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { ResourceBar } from './ResourceBar';
import { SiblingRow } from './shell/SiblingRow';
import { MobileTabBar } from './shell/MobileTabBar';
import { AccountIconButton } from './shell/AccountIconButton';
import { useAppShellPlatform } from '../hooks/useAppShellPlatform';
import { useAuth } from '../contexts/AuthContext';
import { findSiblingGroup } from '../constants/siblingGroups';
import { COLORS } from '../constants/theme';

// SF-122 Note 8: `Layout.tsx` no longer mounts its own `<ProvinceProvider>` — province state is
// now hoisted to a shared `ProvinceRoot` wrapper route element in `App.tsx` (see Component 0),
// covering both `<Layout>` and `<AppShellLayout>` route groups so crossing between the two chrome
// trees doesn't remount province state or re-flash the loading spinner.
//
// SF-128 Component 6: gating mechanism is a single JS branch, not CSS — `platform` and
// `isSiblingRoute` are computed once and every piece of new chrome below is conditioned on
// `platform === 'mobile' && isSiblingRoute` (or `!isSiblingRoute` for the Home-return/Profile/
// Logout suppression — SF-129 extends this to Profile/Logout, same rationale as the arrow).
// Mirrors `AppShellLayout`'s own `{platform === 'mobile' && <MobileTabBar />}` idiom and the
// project's collapsible-panel convention (`{isOpen && <Panel />}`, not CSS `display:none`).
// SF-146: `isSiblingRoute` is now derived from `findSiblingGroup(pathname)` — originally gated on
// the four Realm-only `SIBLING_ROW_ROUTES` pages, now generalized across every registered
// `SiblingGroup` (Realm, War, and future Kingdom/World follow-ups).
export function Layout() {
  const { logout } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  // SF-138 Key Implementation Note 7: `logoutUser()` (api/auth.ts) already swallows its own
  // network errors internally, so `logout()` itself can never reject — no catch needed here.
  // The one real gap is a double-tap firing `logout()` twice before the first call's
  // `navigate('/')` completes; guarded via a local in-flight flag, not native `disabled`
  // (CLAUDE.md's aria-disabled convention — keeps the button visible/discoverable).
  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    await logout();
  };
  const platform = useAppShellPlatform();
  const { pathname } = useLocation();
  const activeSiblingGroup = findSiblingGroup(pathname);
  const isSiblingRoute = activeSiblingGroup !== undefined;
  const showSiblingChrome = platform === 'mobile' && isSiblingRoute;

  // SF-132: reset scroll position to top on lateral SiblingRow navigation (Buildings/Explore/
  // Sciences/Military) — closes the SF-128-deferred item. Legacy Layout.tsx scrolls the
  // document (CLAUDE.md), not an inner container, so `window.scrollTo` is the right target.
  // Scoped to showSiblingChrome so it never fires for non-sibling routes or on desktop.
  useEffect(() => {
    if (showSiblingChrome) {
      window.scrollTo(0, 0);
    }
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen" style={{ backgroundColor: COLORS.bg }}>
      {/* Centered sidebar+content block */}
      <div className="flex min-h-screen mx-auto max-w-[1200px]">
        <Sidebar />

        {/* Content column — ResourceBar + scrollable page content */}
        <div className="flex-1 flex flex-col min-w-0">
          <ResourceBar reserveChromeGutters={!isSiblingRoute} mobileTabBarPresent={showSiblingChrome} />
          {/* SF-122 Key Implementation Note 8 / SF-128 Key Implementation Note 2: one-tap way
              back to the new app shell for legacy-Layout pages with no bottom tab bar of their
              own. Suppressed on every sibling-group route (SF-146: findSiblingGroup-resolved,
              was the four Realm-only SIBLING_ROW_ROUTES pages) — the persistent MobileTabBar
              (below) makes it redundant there, Home being one tap away in the bar. */}
          {!isSiblingRoute && (
            <Link
              to="/province"
              className="lg:hidden fixed top-4 left-4 z-30 p-2 rounded-md hover:text-white"
              style={{ backgroundColor: '#0b0e19', color: '#7b83a5' }}
              aria-label="Back to Throne Room"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 12l9-9 9 9M5 10v10a1 1 0 001 1h4v-6h4v6h4a1 1 0 001-1V10"
                />
              </svg>
            </Link>
          )}

          {/* SF-129 Component 1: interim mobile Profile/Logout access. Suppressed on every
              sibling-group route (SF-146: findSiblingGroup-resolved, was the four Realm-only
              SIBLING_ROW_ROUTES pages) for the same reason as Home-return above — a fixed
              top-right button would sit on top of SiblingRow's sticky right edge once
              stuck/scrolled (CLAUDE.md "Legacy Layout.tsx scrolls the document" note). SF-138:
              this is what makes Profile/Logout actually reachable from every AppShellLayout
              mobile hub (via that layout's own header) — this legacy-Layout affordance stays
              scoped to non-sibling routes here, unchanged. */}
          {!isSiblingRoute && (
            <>
              <AccountIconButton
                icon="logout"
                onClick={handleLogout}
                ariaLabel="Logout"
                ariaDisabled={isLoggingOut}
                className="lg:hidden fixed top-4 right-16 z-30 rounded-md"
              />
              <AccountIconButton
                icon="profile"
                to="/profile"
                ariaLabel="Profile"
                className="lg:hidden fixed top-4 right-4 z-30 rounded-md"
              />
            </>
          )}

          {showSiblingChrome && activeSiblingGroup && (
            <SiblingRow
              items={activeSiblingGroup.items}
              activeRoute={pathname}
              ariaLabel={`${activeSiblingGroup.label} sections`}
            />
          )}

          <main className="flex-1 overflow-y-auto">
            <div
              className="max-w-[1040px] mx-auto"
              style={{ padding: '20px 24px', ...(showSiblingChrome ? { paddingBottom: '96px' } : {}) }}
            >
              <Outlet />
            </div>
          </main>

          {showSiblingChrome && <MobileTabBar />}
        </div>
      </div>
    </div>
  );
}
